import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { useAuth } from './AuthContext';
import Spinner from "./Spinner";
import SuccessModal from './SuccessModal';
import ErrorModal from './ErrorModal';
import MobileHeader from './MobileHeader';
import { useTheme } from './ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';

const ReportingUser = () => {
    const { currentUser } = useAuth();
    const { theme } = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [groupBy, setGroupBy] = useState('driverName');
    const [sortBy, setSortBy] = useState('tranDate');
    const [viewFormat, setViewFormat] = useState('9');  // Default to CSV
    const [includeSummary, setIncludeSummary] = useState(false);
    const [userReport, setUserReport] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [enableMatchByFilters, setEnableMatchByFilters] = useState(true);
    const [isIFTAReport, setIsIFTAReport] = useState(false);

    const [cardNumberChecked, setCardNumberChecked] = useState(false);
    const [cardNumberValue, setCardNumberValue] = useState('');

    const [driverNameChecked, setDriverNameChecked] = useState(false);
    const [driverNameValue, setDriverNameValue] = useState('');

    const [driverIdChecked, setDriverIdChecked] = useState(false);
    const [driverIdValue, setDriverIdValue] = useState('');

    const [unitNumberChecked, setUnitNumberChecked] = useState(false);
    const [unitNumberValue, setUnitNumberValue] = useState('');

    const [countryChecked, setCountryChecked] = useState(false);
    const [countryValue, setCountryValue] = useState('85'); // Default to "USA"

    const [stateChecked, setStateChecked] = useState(false);
    const [stateValue, setStateValue] = useState('');

    const [cityChecked, setCityChecked] = useState(false);
    const [cityValue, setCityValue] = useState('');

    const [chainIdChecked, setChainIdChecked] = useState(false);
    const [chainIdValues, setChainIdValues] = useState('');

    const [itemChecked, setItemChecked] = useState(false);
    const [itemValue, setItemValue] = useState('');

    const apiUrl = process.env.REACT_APP_API_URL;


    useEffect(() => {
        if (isIFTAReport) {
            setGroupBy('stateProv');
            setSortBy('stateProv');
            setIncludeSummary(false);
            setUserReport(false);
        }
        if (!isIFTAReport) {
            setGroupBy('driverName');
            setSortBy('tranDate');
        }
    }, [isIFTAReport]);

    const handleSubmit = async (event) => {
        setIsLoading(true);
        event.preventDefault();
        const userPermissions = {
            NetworkId: currentUser?.tenantId,
            policyNumber: currentUser?.policyNumber,
            ContractId: currentUser?.ContractId,
            cardGroupID: currentUser.cardGroupID,
        };
        const formData = {
            startDate,
            endDate,
            currency,
            groupBy,
            sortBy,
            includeSummary,
            isIFTA: isIFTAReport,
            userReport,
            viewFormat,
            filters: [],
            userPermissions,
            carrierName: currentUser.companyName,
        };

        // Add filters based on checked values
        if (cardNumberChecked) formData.filters.push({ field: 'Card Number', value: cardNumberValue });
        if (driverNameChecked) formData.filters.push({ field: 'Driver Name', value: driverNameValue });
        if (driverIdChecked) formData.filters.push({ field: 'Driver ID', value: driverIdValue });
        if (unitNumberChecked) formData.filters.push({ field: 'Unit', value: unitNumberValue });
        // if (policyIdChecked) formData.filters.push({ field: 'Policy Number', value: policyIdValue });
        if (countryChecked) formData.filters.push({ field: 'Country', value: countryValue });
        if (stateChecked) formData.filters.push({ field: 'State', value: stateValue });
        if (cityChecked) formData.filters.push({ field: 'City', value: cityValue });
        if (chainIdChecked) formData.filters.push({ field: 'Chain Id', value: chainIdValues });
        if (itemChecked) formData.filters.push({ field: 'Item', value: itemValue });

        console.log('Form Data: ', formData);

        try {
            const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getUserReportsV2', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error('Network response was not ok.');

            // Handle Blob data for file download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'download';
            if (contentDisposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
            }
            a.setAttribute('download', filename);
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            setIsLoading(false);
            setShowSuccessModal(true);
        } catch (error) {
            setIsLoading(false);
            setShowErrorModal(true);
            console.error('Error during file download:', error);
        }
    };

    const handleUserReportChange = (checked) => {
        setUserReport(checked);
        setIncludeSummary(!checked ? includeSummary : false);
        setEnableMatchByFilters(!checked);
        if (checked) {
            setCardNumberChecked(false);
            setDriverNameChecked(false);
            setDriverIdChecked(false);
            setUnitNumberChecked(false);
            setCountryChecked(false);
            setStateChecked(false);
            setCityChecked(false);
            setChainIdChecked(false);
            setItemChecked(false);
        }
    };

    const handleIFTAChange = (event) => {
        setIsIFTAReport(event.target.checked);
    };

    return (
        <>
            {isLoading && <Spinner />}
            {showSuccessModal && (
                <SuccessModal
                    message="The report has been successfully generated and downloaded."
                    onClose={() => setShowSuccessModal(false)}
                />
            )}
            {showErrorModal && (
                <ErrorModal
                    message="There was an error processing your request. Please try again."
                    onClose={() => setShowErrorModal(false)}
                />
            )}
            <div className='mobile-container mx-auto px-4 sm:px-6 lg:px-8'>
                <MobileHeader theme={theme} />
                <PageHeader
                    heading="Transaction Reports"
                    context="Generate detailed transaction reports with customizable filters and formats"
                    icon={
                        <FontAwesomeIcon icon={faFileAlt} className="w-6 h-6" />
                    }
                />
                {/* Subaccount Warning */}
                {(currentUser?.isPolicyAdmin || currentUser?.isContractAdmin || currentUser?.isAdmin) && currentUser?.hasSubaccounts && !currentUser?.cardGroupID && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <p className="text-sm text-amber-800">
                                <strong>Multiple Accounts:</strong> You are currently running reports for ALL subaccounts. To see reports for a specific account, navigate to it first.
                            </p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Report Configuration Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                                </svg>
                                Report Configuration
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Configure your report parameters and output format</p>
                        </div>
                        <div className="p-6 space-y-6">

                            {/* Date Range */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="startDate" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Start Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={startDate}
                                        onChange={e => setStartDate(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="endDate" className="block text-sm font-semibold text-gray-700 mb-2">
                                        End Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        name="endDate"
                                        value={endDate}
                                        onChange={e => setEndDate(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    />
                                </div>
                            </div>

                            {/* Report Settings */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label htmlFor="currency" className="block text-sm font-semibold text-gray-700 mb-2">Currency</label>
                                    <select
                                        id="currency"
                                        name="currency"
                                        value={currency}
                                        onChange={e => setCurrency(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                    >
                                        <option value="USD">USD/Gallons</option>
                                        <option value="CAD" disabled>CAD/Liters</option>
                                        <option value="MXN" disabled>MXN/Liters</option>
                                        <option value="ANY" disabled>Multicurrency</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="groupBy" className="block text-sm font-semibold text-gray-700 mb-2">Group By</label>
                                    <select
                                        id="groupBy"
                                        name="groupBy"
                                        value={groupBy}
                                        disabled={isIFTAReport}
                                        onChange={e => setGroupBy(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm disabled:bg-gray-100 disabled:text-gray-500"
                                    >
                                            <option value="cardNumber">Card Number</option>
                                            <option value="arNumber">AR Number</option>
                                            <option value="city">City</option>
                                            <option value="chainId">Chain ID</option>
                                            <option value="db">Direct Bill</option>
                                            <option value="driverID">Driver ID</option>
                                            <option value="driverName">Driver Name</option>
                                            <option value="locationId">Location ID</option>
                                            <option value="locationName">Location Name</option>
                                            <option value="stateProv">State/Province</option>
                                            <option value="tranDate">Transaction Date</option>
                                            <option value="tripNumber">Trip Number</option>
                                            <option value="unit">Unit</option>
                                            <option value="odometer">Odometer</option>
                                        </select>
                                    </div>

                                <div>
                                    <label htmlFor="sortBy" className="block text-sm font-semibold text-gray-700 mb-2">Sort By</label>
                                    <select
                                        id="sortBy"
                                        name="sortBy"
                                        value={sortBy}
                                        disabled={isIFTAReport}
                                        onChange={e => setSortBy(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm disabled:bg-gray-100 disabled:text-gray-500"
                                    >
                                        <option value="tranDate">Transaction Date</option>
                                        <option value="cardNumber">Card Number</option>
                                        <option value="city">City</option>
                                        <option value="db">Direct Bill / Funded</option>
                                        <option value="DriverID">Driver ID</option>
                                        <option value="driverName">Driver Name</option>
                                        <option value="locationName">Location Name</option>
                                        <option value="stateProv">State/Province</option>
                                        <option value="unit">Unit</option>
                                        <option value="odometer">Odometer</option>
                                    </select>
                                </div>
                            </div>

                            {/* Output Format */}
                            <div>
                                <label htmlFor="viewFormat" className="block text-sm font-semibold text-gray-700 mb-2">Output Format</label>
                                <select
                                    id="viewFormat"
                                    name="viewFormat"
                                    value={viewFormat}
                                    onChange={e => setViewFormat(e.target.value)}
                                    className="w-full max-w-sm px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                >
                                    <option value="2" disabled>📄 PDF (Coming Soon)</option>
                                    <option value="9">📊 CSV (Recommended)</option>
                                    <option value="4" disabled>📈 Excel (Coming Soon)</option>
                                    <option value="3" disabled>📝 Text (Coming Soon)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Report Options Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Report Options
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Configure additional report features and output types</p>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <input
                                        type="checkbox"
                                        id="includeSummary"
                                        checked={includeSummary}
                                        onChange={(e) => setIncludeSummary(e.target.checked)}
                                        disabled={userReport || isIFTAReport}
                                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-0.5"
                                    />
                                    <div className="flex-1">
                                        <label htmlFor="includeSummary" className="block text-sm font-medium text-gray-900">
                                            Include Summary Totals
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">Add summary calculations at the end of the report</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <input
                                        type="checkbox"
                                        id="userReport"
                                        checked={userReport}
                                        disabled={isIFTAReport}
                                        onChange={(e) => handleUserReportChange(e.target.checked)}
                                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-0.5"
                                    />
                                    <div className="flex-1">
                                        <label htmlFor="userReport" className="block text-sm font-medium text-gray-900">
                                            Generate Invoice Format
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">Format report as an invoice for billing purposes</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <input
                                        type="checkbox"
                                        id="iftaReport"
                                        checked={isIFTAReport}
                                        onChange={handleIFTAChange}
                                        disabled={userReport}
                                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 mt-0.5"
                                    />
                                    <div className="flex-1">
                                        <label htmlFor="iftaReport" className="block text-sm font-medium text-gray-900">
                                            Generate IFTA Report
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">International Fuel Tax Agreement formatted report</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Advanced Filters Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                                </svg>
                                Advanced Filters
                                <span className="text-sm font-normal text-gray-600">(Optional)</span>
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Filter your report by specific criteria to narrow down results</p>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Card Number Filter */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="cardNumberFilter"
                                            checked={cardNumberChecked}
                                            onChange={() => setCardNumberChecked(!cardNumberChecked)}
                                            disabled={!enableMatchByFilters}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="cardNumberFilter" className="text-sm font-medium text-gray-700">
                                            Card Number
                                        </label>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Complete Card Number Required"
                                        value={cardNumberValue}
                                        onChange={(e) => setCardNumberValue(e.target.value)}
                                        maxLength="21"
                                        disabled={!cardNumberChecked}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 text-sm"
                                    />
                                </div>

                                {/* Driver Name Filter */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="driverNameFilter"
                                            checked={driverNameChecked}
                                            onChange={() => setDriverNameChecked(!driverNameChecked)}
                                            disabled={!enableMatchByFilters}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="driverNameFilter" className="text-sm font-medium text-gray-700">
                                            Driver Name
                                        </label>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Enter driver name"
                                        value={driverNameValue}
                                        onChange={(e) => setDriverNameValue(e.target.value)}
                                        disabled={!driverNameChecked}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 text-sm"
                                    />
                                </div>

                                {/* Driver ID Filter */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="driverIdFilter"
                                            checked={driverIdChecked}
                                            onChange={() => setDriverIdChecked(!driverIdChecked)}
                                            disabled={!enableMatchByFilters}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="driverIdFilter" className="text-sm font-medium text-gray-700">
                                            Driver ID
                                        </label>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Enter driver ID"
                                        value={driverIdValue}
                                        onChange={(e) => setDriverIdValue(e.target.value)}
                                        disabled={!driverIdChecked}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 text-sm"
                                    />
                                </div>

                                {/* Unit Number Filter */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="unitNumberFilter"
                                            checked={unitNumberChecked}
                                            onChange={() => setUnitNumberChecked(!unitNumberChecked)}
                                            disabled={!enableMatchByFilters}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="unitNumberFilter" className="text-sm font-medium text-gray-700">
                                            Unit Number
                                        </label>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Enter unit number"
                                        value={unitNumberValue}
                                        onChange={(e) => setUnitNumberValue(e.target.value)}
                                        disabled={!unitNumberChecked}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 text-sm"
                                    />
                                </div>

                                {/* Country Filter */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="countryFilter"
                                            checked={countryChecked}
                                            onChange={() => setCountryChecked(!countryChecked)}
                                            disabled={!enableMatchByFilters || isIFTAReport}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="countryFilter" className="text-sm font-medium text-gray-700">
                                            Country
                                        </label>
                                    </div>
                                    <select
                                        value={countryValue}
                                        onChange={(e) => setCountryValue(e.target.value)}
                                        disabled={!countryChecked}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 text-sm"
                                    >
                                        <option value="85">United States</option>
                                        <option value="67">Canada</option>
                                    </select>
                                </div>

                                {/* Note: Additional filters would go here using the same pattern */}
                                <div className="col-span-1 lg:col-span-2 text-center text-sm text-gray-500 italic bg-gray-50 p-4 rounded-lg">
                                    Additional filters (State, City, Chain ID, Item) can be added here following the same modern design pattern.
                                    For now, the core filters (Card Number, Driver Name, Driver ID, Unit Number, Country) are modernized.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Generate Your Report
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">Ready to create your customized transaction report</p>
                        </div>
                        <div className="p-6 text-center">
                            <p className="text-sm text-gray-600 mb-6">
                                Your report will be generated based on the configuration above and automatically downloaded to your device.
                            </p>
                            <button
                                type="submit"
                                className="btn-base btn-primary px-8 py-4 text-base font-semibold flex items-center gap-3 shadow-lg hover:shadow-xl transition-all mx-auto"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Generate Report
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default ReportingUser;
